import { connectorsByIdentifier } from "./index";
import type { ConnectorOAuthConfig } from "./types";

type DcrOAuthConfig = Extract<
  ConnectorOAuthConfig,
  { clientCredentialsFrom: "dcr" }
>;

export function isValidDcrEndpoint(
  value: string | undefined,
  serverUrl: string
) {
  const protocol = URL.parse(value ?? "")?.protocol;
  return (
    protocol === "https:" ||
    (protocol === "http:" && URL.parse(serverUrl)?.protocol === "http:")
  );
}

export function hasDcrConfiguration(
  oauth: ConnectorOAuthConfig,
  configuration: Record<string, string>,
  clientId: string | null | undefined
): boolean {
  return (
    oauth.clientCredentialsFrom === "dcr" &&
    !!clientId &&
    isValidDcrEndpoint(
      oauth.authorizationEndpoint?.(configuration),
      oauth.discoverFrom(configuration)
    ) &&
    isValidDcrEndpoint(
      oauth.tokenEndpoint(configuration),
      oauth.discoverFrom(configuration)
    )
  );
}

export function dcrClientPending(
  oauth: ConnectorOAuthConfig | undefined,
  configuration: Record<string, string>,
  clientId: string | null | undefined
) {
  return (
    oauth?.clientCredentialsFrom === "dcr" &&
    !hasDcrConfiguration(oauth, configuration, clientId)
  );
}

export function dcrInputsChanged(
  oauth: DcrOAuthConfig,
  before: Record<string, string>,
  after: Record<string, string>
) {
  return (
    oauth.discoverFrom(before) !== oauth.discoverFrom(after) ||
    before.authMode !== after.authMode ||
    before.caCertificate !== after.caCertificate
  );
}

export function dcrServerChanged(
  before: { identifier: string; configuration: Record<string, string> },
  after: { identifier: string; configuration: Record<string, string> }
) {
  const oauth = connectorsByIdentifier[after.identifier]?.oauth;
  return (
    before.identifier === after.identifier &&
    oauth?.clientCredentialsFrom === "dcr" &&
    oauth.discoverFrom(before.configuration) !==
      oauth.discoverFrom(after.configuration)
  );
}

const DISCOVERED_CONFIGURATION_KEYS = [
  "authorizeUrl",
  "tokenUrl",
  "issuer",
  "resource",
  "discoveredScopes",
];

export function withoutDiscovery(configuration: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(configuration).filter(
      ([key]) => !DISCOVERED_CONFIGURATION_KEYS.includes(key)
    )
  );
}
